import { SliceApiError, type AiAction, type AttendanceStatus, type RollCallMass } from './types'
import type { VoiceProblem } from './useVoiceInput'

/**
 * Перекличка текстом и голосом на экране занятия (этап 5, шаг 2): тексты
 * состояний без интерфейса. Подписи без рода, только дефис.
 */

export const MARK_LABEL: Record<AttendanceStatus, string> = {
  present: 'Есть',
  absent: 'Нет',
  late: 'Опоздание',
  left_early: 'Ранний уход',
  sick: 'Болезнь',
  excused: 'Уважительная причина',
}

/** «Есть», «Болезнь (температура)», «Не отмечено». */
export function markText(status: AttendanceStatus | null, notes?: string): string {
  if (!status) return 'Не отмечено'
  return notes ? `${MARK_LABEL[status]} (${notes})` : MARK_LABEL[status]
}

/** Что массовая перекличка делает с теми, кого не назвали. */
export const MASS_TEXT: Record<RollCallMass, string> = {
  rest_absent: '«Остальных нет»: всем без отметки встанет «Нет».',
  all_except: '«Все, кроме»: всем, кроме названных, встанет «Есть».',
  all_present: '«Все здесь»: всем встанет «Есть».',
}

/** Почему массовая перекличка с непонятыми именами не готовится. */
export const BLOCKED_TEXT: Record<RollCallMass, string> = {
  rest_absent: 'При «остальных нет» непонятый спортсмен получил бы «Нет».',
  all_except: 'При «все, кроме» непонятое исключение получило бы «Есть».',
  all_present: 'При «все здесь» непонятая отметка заменилась бы на «Есть».',
}

/** Микрофон на экране переклички: те же причины, что в Штабе, другой запасной путь. */
export const ROLL_VOICE_PROBLEM: Record<VoiceProblem, string> = {
  unsupported: 'Этот браузер не умеет записывать голос. Напишите перекличку текстом.',
  denied:
    'Нет доступа к микрофону. Разрешить его можно в настройках браузера для этого сайта. Текстом перекличка работает и без микрофона.',
  no_device: 'Микрофон не найден. Напишите перекличку текстом.',
  busy: 'Микрофон занят другим приложением. Освободите его и попробуйте ещё раз.',
  interrupted: 'Запись прервалась и не распознана. Запишите ещё раз.',
  too_short: 'Запись слишком короткая. Нажмите «Сказать голосом», назовите, кто есть и кого нет, и нажмите «Готово».',
}

/**
 * Сбой разбора или распознавания:
 * - offline - нет сети, повтор вручную, когда она появится (решение 7а: не копим);
 * - limit - подписка закончилась, повтор не поможет, путь в подписку;
 * - retry - сервер не справился, слишком часто или отметки изменились: повтор
 *   безопасен - те же отметки дважды не запишутся;
 * - gone - занятия или группы больше нет.
 */
export interface RollCallProblem {
  kind: 'offline' | 'limit' | 'retry' | 'gone'
  text: string
}

export function previewProblem(err: unknown): RollCallProblem {
  const e = err instanceof SliceApiError ? err : null
  if (!e || e.kind === 'network') {
    return { kind: 'offline', text: 'Нет сети - перекличку не отправить. Текст остался на экране, отправьте, когда появится сеть.' }
  }
  if (e.message === 'rate_limited') {
    return { kind: 'retry', text: 'Слишком много разборов подряд. Подождите минуту и повторите. Отметки тапами работают и сейчас.' }
  }
  if (e.message === 'roll_call_stale') {
    return { kind: 'retry', text: 'Пока разбирал, отметки на занятии изменились. Ничего не записано, разберите ещё раз.' }
  }
  if (e.kind === 'limit') {
    return { kind: 'limit', text: 'Подписка клуба закончилась: клуб только для чтения, отметки не записать.' }
  }
  if (e.kind === 'not_found') return { kind: 'gone', text: 'Занятие не найдено: возможно, группу удалили или занятие перенесли.' }
  return { kind: 'retry', text: 'Не получилось разобрать перекличку. Повторить безопасно: те же отметки дважды не запишутся.' }
}

export function transcribeProblem(err: unknown): RollCallProblem {
  const e = err instanceof SliceApiError ? err : null
  if (!e || e.kind === 'network') {
    return { kind: 'offline', text: 'Нет сети - запись не распознать. Она сохранится, пока экран открыт: распознайте, когда появится сеть.' }
  }
  if (e.message === 'transcription failed') {
    return { kind: 'retry', text: 'Не распознано: слов не разобрать. Запишите ещё раз или напишите текстом.' }
  }
  return { kind: 'retry', text: 'Не получилось распознать запись. Повторите или напишите текстом.' }
}

/** Итог подтверждения: что сказать и можно ли разобрать заново. */
export interface ConfirmOutcome {
  ok: boolean
  text: string
  /** Ничего не записано, разбор устарел: «Разобрать заново». */
  reparse: boolean
}

export function confirmOutcome(a: AiAction, replayed: boolean): ConfirmOutcome {
  switch (a.status) {
    case 'executed':
      return {
        ok: true,
        reparse: false,
        text: replayed ? 'Уже записано раньше. Повторно не записывается.' : `Записано отметок: ${a.result?.done.length ?? a.items.length}.`,
      }
    case 'executing':
      return { ok: true, reparse: false, text: 'Отметки записываются. Подтверждать ещё раз не нужно.' }
    case 'expired':
      return { ok: false, reparse: true, text: 'Проверка устарела: прошло больше 30 минут. Ничего не записано, разберите заново.' }
    case 'cancelled':
      return { ok: false, reparse: true, text: 'Перекличка отменена. Ничего не записано.' }
    case 'failed':
      if (a.errorCode === 'stale') {
        const what = (a.result?.failed ?? []).map((f) => (f.detail ? `${f.label} - ${f.detail}` : f.label)).join('; ')
        return {
          ok: false,
          reparse: true,
          text: `Пока проверяли, отметки изменились${what ? `: ${what}` : ''}. Ничего не записано, разберите заново.`,
        }
      }
      if (a.errorCode === 'forbidden') {
        return { ok: false, reparse: false, text: 'Нет доступа к группе: права изменились. Ничего не записано.' }
      }
      if (a.errorCode === 'interrupted') {
        return { ok: false, reparse: false, text: 'Запись прервалась. Часть отметок могла сохраниться - проверьте на экране занятия.' }
      }
      return { ok: false, reparse: true, text: 'Не удалось записать отметки.' }
    case 'pending':
      return { ok: false, reparse: false, text: 'Пока не записано.' }
  }
}

/** «0:07» */
export function formatSeconds(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}
